import BN from "bignumber.js";
import ApiRequest from "@/common/lib/apiRequest";
import AppConst from "@/common/constants";
import { hexlify } from "@/common/lib/utils";
import ERC20ABI from "@/common/config/abi/ERC20.json";

const mutationTypes = {
  SET_AVAILABLE_BALANCE: "skale/SET_AVAILABLE_BALANCE",
  SET_ESTIMATED_APY: "skale/SET_ESTIMATED_APY",
  SET_NETWORK_INFO: "skale/SET_NETWORK_INFO",
  SET_DELEGATIONS: "skale/SET_DELEGATIONS",
  SET_EARNED_BOUNTY: "skale/SET_EARNED_BOUNTY",
  SET_TX_ACTION: "skale/SET_TX_ACTION",
  RESET_STATE: "skale/RESET_STATE",
  SET_PRICE_CHART_DATA: "skale/SET_PRICE_CHART_DATA",
  SET_STAKED_INFO: "skale/SET_STAKED_INFO",
};

const createStakingState = () => ({
  availableBalance: {
    result: null,
  },
  delegations: {
    result: null,
  },
  rewards: {
    result: null,
  },
  stakingBalances: {
    delegated: 0, // DELEGATED
    pending: 0, // ACCEPTED + PROPOSED
    unbonding: 0, // UNDELEGATION_REQUESTED,
    accepted: 0, // ACCEPTED
    proposed: 0, // PROPOSED
  },
  currentTxAction: "",
});

const state = {
  estimatedApy: {
    result: null,
  },
  networkInfo: {
    result: null,
  },
  assetPriceChart: {
    fetching: false,
    result: [],
  },
  stakedInfo: {
    result: null,
  },
  ...createStakingState(),
};

const actions = {
  setTxAction({ commit }, action) {
    commit(mutationTypes.SET_TX_ACTION, action);
  },
  resetState({ commit }) {
    commit(mutationTypes.RESET_STATE);
  },
  async fetchRequiredInfo({ dispatch, rootGetters }) {
    if (rootGetters["userWallets/walletSelected"].coin_network !== "ethereum") {
      return;
    }
    await Promise.all([
      dispatch("fetchAvailableBalance"),
      dispatch("fetchDelegations"),
    ]);
    await dispatch("fetchRewards");
  },
  async fetchAdditionalInfo({ dispatch }) {
    return Promise.all([
      dispatch("fetchEstimatedApy"),
      dispatch("fetchAssetPriceChart"),
      dispatch("fetchStakedInfo"),
    ]);
  },
  async fetchStakedInfo({ commit }) {
    try {
      const result = await ApiRequest.get(`/eth/skale/total_staked`);
      commit(mutationTypes.SET_STAKED_INFO, {
        result,
      });
    } catch (error) {
      commit(mutationTypes.SET_STAKED_INFO, { error });
    }
  },
  async fetchEstimatedApy({ commit }) {
    try {
      const result = await ApiRequest.get(`/eth/skale/apy`);
      commit(mutationTypes.SET_ESTIMATED_APY, {
        result: {
          value: result.apy * 100,
        },
      });
    } catch (error) {
      commit(mutationTypes.SET_ESTIMATED_APY, { error });
    }
  },
  async fetchNetworkInfo({ commit }) {
    try {
      const result = await ApiRequest.get(`/eth/skale/validator/swu`);
      commit(mutationTypes.SET_NETWORK_INFO, { result });
    } catch (error) {
      commit(mutationTypes.SET_NETWORK_INFO, { error });
    }
  },
  async fetchAssetPriceChart({ commit }, { startTime, type = "h" } = {}) {
    commit(mutationTypes.SET_PRICE_CHART_DATA, { fetching: true });
    try {
      startTime = startTime || Date.now() - 86400000;
      startTime = Math.floor(startTime / 1000);
      const data = await ApiRequest.get("/eth/skale/skl_prices/" + startTime, {
        params: { type },
      });
      const result = getPriceChartData(data, type);
      commit(mutationTypes.SET_PRICE_CHART_DATA, { result, fetching: false });
      return result;
    } catch (error) {
      commit(mutationTypes.SET_PRICE_CHART_DATA, { error, fetching: false });
    }
  },
  async fetchAvailableBalance({ commit, rootGetters }) {
    const address = rootGetters["userWallets/walletSelected"].address;
    commit(mutationTypes.SET_AVAILABLE_BALANCE, { fetching: true });
    try {
      const result = await ApiRequest.get(
        "/eth/skale/delegator/available_balance/" + address
      );
      commit(mutationTypes.SET_AVAILABLE_BALANCE, { result, fetching: false });
    } catch (error) {
      commit(mutationTypes.SET_AVAILABLE_BALANCE, { error, fetching: false });
    }
  },
  async fetchDelegations({ commit, rootGetters }) {
    const address = rootGetters["userWallets/walletSelected"].address;
    commit(mutationTypes.SET_DELEGATIONS, { fetching: true });
    try {
      const result = await ApiRequest.get(
        "/eth/skale/delegator/delegations/" + address
      );
      commit(mutationTypes.SET_DELEGATIONS, { result, fetching: false });
    } catch (error) {
      commit(mutationTypes.SET_DELEGATIONS, { error, fetching: false });
    }
  },
  async fetchRewards({ commit, rootGetters, state }) {
    commit(mutationTypes.SET_EARNED_BOUNTY, { fetching: true });
    const address = rootGetters["userWallets/walletSelected"].address;
    const validatorIds = Array.from(
      new Set(
        state.delegations.result
          .filter(item =>
            ["DELEGATED", "COMPLETED", "UNDELEGATION_REQUESTED"].includes(
              item.state
            )
          )
          .map(item => item.validator.validatorId + "_" + item.validator.name)
      )
    );
    try {
      const rewards = await Promise.all(
        validatorIds.map(async identity => {
          const [id, name] = identity.split("_");
          const data = await ApiRequest.get(
            `/eth/skale/delegator/earned_bounty/${address}?validator_id=${id}`
          );
          return {
            id,
            name,
            rewards: data,
          };
        })
      );
      const claimed = BN.sum(
        ...rewards.map(item => item.rewards.claimed)
      ).toNumber();
      const unclaimed = BN.sum(
        ...rewards.map(item => item.rewards.unclaimed)
      ).toNumber();
      const result = {
        claimed: claimed,
        value: unclaimed,
        rewards,
      };
      commit(mutationTypes.SET_EARNED_BOUNTY, { result, fetching: false });
    } catch (error) {
      commit(mutationTypes.SET_EARNED_BOUNTY, { error, fetching: false });
    }
  },
  async getAllowance({ rootGetters }) {
    const address = rootGetters["userWallets/walletSelected"].address;
    const result = await ApiRequest.get(
      "/eth/skale/delegator/allowance/" + address
    );
    return result.value.value;
  },
  async getNonce({ rootGetters }) {
    const address = rootGetters["userWallets/walletSelected"].address;
    const result = await ApiRequest.get(`/eth/skale/nonce/${address}`);
    return result.value;
  },
  async getApproveRawTx({ rootGetters, dispatch }, { amount }) {
    const address = rootGetters["userWallets/walletSelected"].address;
    const maxPriorityFeePerGas = rootGetters["appConfig/maxPriorityFeePerGas"];
    const result = await ApiRequest.get(
      `/eth/skale/delegator/digest/approve/${address}`,
      { params: { amount } }
    );
    const [nonce, { fast }] = await Promise.all([
      dispatch("getNonce"),
      dispatch("appConfig/fetchEthGasPrice", null, { root: true }),
    ]);
    const tx = {
      from: address,
      to: result.contractAddress,
      maxFeePerGas: hexlify(fast * 1e9),
      maxPriorityFeePerGas: hexlify(maxPriorityFeePerGas),
      gas: hexlify(result.gasLimit),
      value: "0x0",
      nonce: hexlify(nonce),
      data: result.digest,
    };
    return tx;
  },
  async getStakeRawTx({ rootGetters, dispatch }, { amount, period }) {
    const address = rootGetters["userWallets/walletSelected"].address;
    const [result, nonce] = await Promise.all([
      ApiRequest.get(`/eth/skale/delegator/digest/delegate/${address}`, {
        params: { amount },
      }),
      dispatch("getNonce"),
    ]);
    const tx = {
      from: address,
      to: result.contractAddress,
      gas: result.gasLimit,
      value: "0x0",
      nonce: hexlify(nonce),
      data: result.digest,
    };
    return tx;
  },
  async getClaimRewardsRawTx(
    { rootGetters, dispatch },
    { validatorId, destinationAddress }
  ) {
    const address = rootGetters["userWallets/walletSelected"].address;
    destinationAddress = destinationAddress || address;
    const [result, nonce] = await Promise.all([
      ApiRequest.get(`/eth/skale/delegator/digest/withdraw_bounty/${address}`, {
        params: { validator_id: validatorId, to: destinationAddress },
      }),
      dispatch("getNonce"),
    ]);
    const tx = {
      from: address,
      to: result.contractAddress,
      gas: result.gasLimit,
      value: "0x0",
      nonce: hexlify(nonce),
      data: result.digest,
    };
    return tx;
  },
  async getSendRawTx({ rootGetters, dispatch }, payload) {
    const maxPriorityFeePerGas = rootGetters["appConfig/maxPriorityFeePerGas"];
    const web3 = rootGetters["appConfig/web3"]();
    const address = rootGetters["userWallets/walletSelected"].address;
    const getTokenByName = rootGetters["appConfig/getTokenByName"];
    const { contractAddress } = getTokenByName("skl");
    const contract = new web3.eth.Contract(ERC20ABI, contractAddress, {
      from: address,
    });
    const transferMethod = contract.methods.transfer(
      payload.toAddress,
      hexlify(payload.amount)
    );
    const [nonce, gasLimit, { fast }] = await Promise.all([
      dispatch("getNonce"),
      transferMethod.estimateGas(),
      dispatch("appConfig/fetchEthGasPrice", null, { root: true }),
    ]);
    const tx = {
      from: address,
      to: contractAddress,
      maxFeePerGas: hexlify(fast * 1e9),
      maxPriorityFeePerGas: hexlify(maxPriorityFeePerGas),
      gas: hexlify(gasLimit),
      value: "0x0",
      nonce: hexlify(nonce),
      data: transferMethod.encodeABI(),
    };
    return tx;
  },
  async getUnstakeRawTx({ rootGetters, dispatch }, { delegationId }) {
    const address = rootGetters["userWallets/walletSelected"].address;
    const [result, nonce] = await Promise.all([
      ApiRequest.get(
        `/eth/skale/delegator/digest/request_undelegation/${address}`,
        {
          params: { delegation_id: delegationId },
        }
      ),
      dispatch("getNonce"),
    ]);
    const tx = {
      from: address,
      to: result.contractAddress,
      gas: result.gasLimit,
      value: "0x0",
      nonce: hexlify(nonce),
      data: result.digest,
    };
    return tx;
  },
  async getCancelRawTx({ rootGetters, dispatch }, { delegationId }) {
    const address = rootGetters["userWallets/walletSelected"].address;
    const [result, nonce] = await Promise.all([
      ApiRequest.get(
        `/eth/skale/delegator/digest/cancel_pending_delegation/${address}`,
        {
          params: { delegation_id: delegationId },
        }
      ),
      dispatch("getNonce"),
    ]);
    const tx = {
      from: address,
      to: result.contractAddress,
      gas: result.gasLimit,
      value: "0x0",
      nonce: hexlify(nonce),
      data: result.digest,
    };
    return tx;
  },
  async broadcastTx({ rootGetters, dispatch }, payload) {
    const walletSelected = rootGetters["userWallets/walletSelected"];
    let url;
    let body = {
      type: payload.type,
      metadata: payload.metadata,
    };
    if (payload.signedTx) {
      body.signed_tx = payload.signedTx;
      url = `/eth/skale/transaction/broadcast/${walletSelected.address}`;
    } else {
      body.tx_hash = payload.txHash;
      url = `/eth/skale/transaction/create/${walletSelected.address}`;
    }
    return ApiRequest.post(url, body);
  },
};

const mutations = {
  [mutationTypes.SET_AVAILABLE_BALANCE](state, data) {
    state.availableBalance = data;
  },
  [mutationTypes.SET_NETWORK_INFO](state, data) {
    state.networkInfo = data;
  },
  [mutationTypes.SET_ESTIMATED_APY](state, data) {
    state.estimatedApy = data;
  },
  [mutationTypes.SET_DELEGATIONS](state, data) {
    state.delegations = data;
    if (data.result?.length) {
      const balances = {
        delegated: 0,
        pending: 0,
        unbonding: 0,
        accepted: 0,
        proposed: 0,
      };
      data.result.forEach(item => {
        switch (item.state) {
          case AppConst.SKALE_TX_STATE.DELEGATED:
            balances.delegated = new BN(balances.delegated)
              .plus(item.amount)
              .toNumber();
            break;
          case AppConst.SKALE_TX_STATE.UNDELEGATION_REQUESTED:
            balances.unbonding = new BN(balances.unbonding)
              .plus(item.amount)
              .toNumber();
            break;
          case AppConst.SKALE_TX_STATE.ACCEPTED:
            balances.accepted = new BN(balances.accepted)
              .plus(item.amount)
              .toNumber();
            break;
          case AppConst.SKALE_TX_STATE.PROPOSED:
            balances.proposed = new BN(balances.proposed)
              .plus(item.amount)
              .toNumber();
            break;
          default:
            break;
        }
      });
      balances.pending = new BN(balances.proposed)
        .plus(balances.accepted)
        .toNumber();
      state.stakingBalances = balances;
    }
  },
  [mutationTypes.SET_EARNED_BOUNTY](state, data) {
    state.rewards = data;
  },
  [mutationTypes.SET_TX_ACTION](state, action) {
    state.currentTxAction = action;
  },
  [mutationTypes.SET_PRICE_CHART_DATA](state, data) {
    state.assetPriceChart = data;
  },
  [mutationTypes.SET_STAKED_INFO](state, data) {
    state.stakedInfo = data;
  },
  [mutationTypes.RESET_STATE](state) {
    const stakingState = createStakingState();
    for (const key in stakingState) {
      state[key] = stakingState[key];
    }
  },
};

const getters = {
  estimatedApy: state => state.estimatedApy,
  networkInfo: state => state.networkInfo,
  assetPriceChart: state => state.assetPriceChart,
  availableBalance: state => state.availableBalance,
  rewards: state => state.rewards,
  delegations: state => state.delegations,
  stakingBalances: state => state.stakingBalances,
  totalAssets: state => {
    return new BN(state.availableBalance.result?.balance ?? 0)
      .plus(state.rewards.result?.value ?? 0)
      .toNumber();
  },
  currentTxAction: state => state.currentTxAction,
  stakedInfo: state => state.stakedInfo,
};

const getPriceChartData = (data, type) => {
  data = data.sort((a, b) => a.timestamp - b.timestamp);
  const result = [];
  for (let index = data.length - 1; index > -1; index -= 1) {
    const elem = data[index];
    result.unshift([elem.timestamp, Number(elem.usd)]);
  }
  if (result.length === 1) return [result[0], result[0]];
  return result;
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
